import React, {
  useCallback, useEffect, useState, useMemo,
} from 'react';
import { Grid } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import EditorJS from '@editorjs/editorjs';
import { useFormContext } from 'react-hook-form';
import uuid from 'uuid';

import { useFormikContext } from 'formik';
import { ImageInstruction, InstructionTypes } from '../../../../types/instructions';
import {
  setOpenImageInstructionModal,
  getImageListRequest,
} from '../../../../store/modules/script-editor/actions';
import { setMediaList, setOpenMediaViewer } from '../../../../store/modules/media-viewer/actions';

import { selectImageInstruction } from '../../../../store/modules/script-editor/selectors';
import { selectProject } from '../../../../store/modules/projects/selectors';
import {
  InstructionEvent,
  InstructionActionTooltip,
} from '../types';
import { getQueryFilter } from '../../../../utility/helpers';
import { ListParams } from '../../../../types/main';
import { getLargeDocumentThumbnailUrl, getSmallDocumentThumbnailUrl } from '../../../../views/projects/tab-media/media/utils';
import { parseJsonToEditorStateWithInstruction } from '../../../../services/editorJS/utils';
import {
  emitInstructionEventByEditor, removeInstructionElement, setInstructionActionTooltip, sortDocumentsByTitle,
} from '../utils';
import {
  ModalComponent,
  InputComponent,
  SelectComponent,
  SmallImageItem,
  ProcessingMedia,
} from '../components';
import useStyles from './styles';
import { ScreenLocations, ImageEffects } from '../../../../configs/instructionConfig';
import DuplicateBlock from '../../../../services/editorJS/DuplicateBlock';
import { notify } from '../../../../store/modules/notifications/actions';
import { FormSimpleAutoComplete } from '../../..';

import { Option } from '../../../form-simple-autocomplete/FormSimpleAutoComplete';
import MediaFileMissingMessage from '../components/MediaFileMissingMessage';

type Props = {
  instanceEditor: EditorJS | undefined;
};

const scriptFieldName = 'script';

const defaultState = {
  comment: '',
  screenLocation: 0,
  imageEffect: 0,
  documentId: -1,
};

const ImageInstructionModal = ({ instanceEditor }: Props) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const { getValues, setValue } = useFormContext();
  const { setFieldValue } = useFormikContext();

  const currentProject = useSelector(selectProject);
  const {
    isOpenModal, instructionId, images, isInline,
  } = useSelector(selectImageInstruction);

  const [comment, setComment] = useState(defaultState.comment);
  const [screenLocation, setScreenLocation] = useState(defaultState.screenLocation);
  const [imageEffect, setImageEffect] = useState(defaultState.imageEffect);
  const [documentId, setDocumentId] = useState(defaultState.documentId);
  const [initialState, setInitialState] = useState(defaultState);

  const showKebabMenu = useMemo(() => {
    const scriptData = parseJsonToEditorStateWithInstruction(getValues()[scriptFieldName]);
    return scriptData.instructions.some(
      (item) => item.id === instructionId,
    ) || scriptData.blocks.some((item) => item.data.instructionId === instructionId);
  }, [getValues, instructionId]);

  const imageList = useMemo(() => {
    if (images.data) {
      const sortedImages = sortDocumentsByTitle(images.data.items).map((item) => {
        const url = getSmallDocumentThumbnailUrl(item.ref.document?.thumbnails || []);
        return {
          id: item.document_id,
          label: item.ref.document!.title,
          title: <SmallImageItem text={item.ref.document?.title || 'Image'} imageUrl={url} />,
        };
      });
      return [{ id: -1, label: 'None' }, ...sortedImages];
    }
    return [];
  }, [images.data]);

  const imageUrl = useMemo(() => {
    if (images.data) {
      const article = images.data.items.find((item) => item.document_id === documentId);
      if (article) {
        return getLargeDocumentThumbnailUrl(article.ref.document?.thumbnails || []);
      }
    }

    return '';
  }, [images.data, documentId]);

  const closeHandler = useCallback(() => {
    if (instanceEditor) {
      emitInstructionEventByEditor(
        instanceEditor,
        InstructionEvent.SET_IMAGE_INSTRUCTION_SELECTION,
        {},
      );
    }
    dispatch(setOpenImageInstructionModal(false));
  }, [dispatch, instanceEditor]);

  const getImageTitle = useCallback(() => {
    if (images.data) {
      if (documentId !== -1) {
        const article = images.data.items.find((item) => item.document_id === documentId);
        if (article) {
          return article.ref.document?.title || 'Image';
        }
      } else if (comment.trim()) {
        return comment;
      }
    }

    return '';
  }, [images, documentId, comment]);

  const getSummary = useCallback(() => getImageTitle() || 'Image', [getImageTitle]);

  const saveImageInstruction = useCallback(() => {
    if (instanceEditor) {
      (async () => {
        const scriptData = parseJsonToEditorStateWithInstruction(getValues()[scriptFieldName]);
        const instructionIndex = scriptData.instructions.findIndex(
          (item) => item.id === instructionId && item.type === InstructionTypes.imageInstruction,
        );
        const imageInstructions: ImageInstruction = {
          id: instructionId,
          type: InstructionTypes.imageInstruction,
          comment,
          needsInfo: false,
          data: {
            documentId,
            imageEffect,
            screenLocation,
          },
        };
        const summary = getSummary();
        if (instructionIndex !== -1) {
          scriptData.instructions[instructionIndex] = imageInstructions;
          if (isInline) {
            setInstructionActionTooltip(
              {
                instructionId,
                tooltip: `${InstructionActionTooltip.image}: ${getSummary()}`,
                scriptBlocks: scriptData.blocks,
              },
            );
            emitInstructionEventByEditor(
              instanceEditor,
              InstructionEvent.UPDATE_IMAGE_ACTION_EVENT_LISTENERS,
              {
                instanceEditor,
                refEditor: window.scriptEditorRef!,
              },
            );
          }
          instanceEditor.events.emit(InstructionEvent.SUBMIT_DATA, {});
        } else {
          const id = (instructionId.trim()) ? instructionId : uuid();
          imageInstructions.id = id;
          scriptData.instructions.push(imageInstructions);
          if (isInline) {
            emitInstructionEventByEditor(
              instanceEditor,
              InstructionEvent.SAVE_IMAGE_INSTRUCTION,
              { instructionId: id, tooltip: getSummary() },
            );
          }
        }
        if (!isInline) {
          emitInstructionEventByEditor(
            instanceEditor,
            InstructionEvent.SAVE_IMAGE_INSTRUCTION_BLOCK,
            { instructionId: imageInstructions.id, text: summary, tooltip: getSummary() },
          );
        }
        setValue(scriptFieldName, JSON.stringify(scriptData));
        closeHandler();
      })();
    }
  }, [
    instanceEditor,
    getValues,
    setValue,
    documentId,

    comment,
    screenLocation,
    imageEffect,
    instructionId,
    closeHandler,
    isInline,
    getSummary,
  ]);


  const cancelImageInstruction = useCallback(() => {
    (async () => {
      setComment(initialState.comment);
      setScreenLocation(initialState.screenLocation);
      setImageEffect(initialState.imageEffect);
      setDocumentId(initialState.documentId);
      const scriptData = parseJsonToEditorStateWithInstruction(getValues()[scriptFieldName]);
      const isInstructionCreated = scriptData.instructions.some(
        (item) => item.id === instructionId && item.type === InstructionTypes.imageInstruction,
      );
      if (instanceEditor) {
        if (!isInstructionCreated) {
          emitInstructionEventByEditor(
            instanceEditor,
            InstructionEvent.REMOVE_INSTRUCTION_BLOCK_WITHOUT_SAVING,
            {},
          );
        }
        emitInstructionEventByEditor(
          instanceEditor,
          InstructionEvent.CANCEL_INSTRUCTION,
          {},
        );
      }
      closeHandler();
    })();
  }, [
    setComment,
    setScreenLocation,
    setImageEffect,
    setDocumentId,
    initialState,
    closeHandler,
    instanceEditor,
    instructionId,
    getValues,
  ]);

  const removeHandler = useCallback(() => {
    if (!isInline && instanceEditor) {
      emitInstructionEventByEditor(
        instanceEditor,
        InstructionEvent.REMOVE_INSTRUCTION_BLOCK,
        {},
      );
    } else {
      removeInstructionElement({ instructionId, isInline })
        .then(() => {
          if (instanceEditor) {
            instanceEditor.events.emit(InstructionEvent.SUBMIT_DATA, {});
          }
        });
    }
    closeHandler();
  }, [closeHandler, instructionId, isInline, instanceEditor]);

  const openPreview = useCallback(() => {
    if (imageUrl) {
      dispatch(setMediaList([{
        url: imageUrl,
        title: 'Preview',
        id: 1,
        typeId: 1,
      }]));
      dispatch(setOpenMediaViewer(true));
    }
  }, [dispatch, imageUrl]);

  const duplicate = useCallback(() => {
    const scriptData = parseJsonToEditorStateWithInstruction(getValues().script);
    const notifHandler = () => dispatch(
      notify({
        type: 'success',
        icon: 'copy',
        message: 'Instruction duplicated.',
      }),
    );
    if (instanceEditor) {
      const duplicateBlockService = new DuplicateBlock(instanceEditor, notifHandler, setValue, instructionId, scriptData);
      if (!isInline) {
        duplicateBlockService.duplicateBlock();
      } else {
        duplicateBlockService.duplicateInline();
      }
    }
  }, [instanceEditor, getValues, instructionId, isInline, setValue, dispatch]);

  const onDocumentChangeHandler = useCallback((option?: Option) => {
    if (option) {
      setDocumentId(parseInt(option.id.toString(), 10));
    }
  }, [setDocumentId]);

  useEffect(() => {
    if (isOpenModal) {
      setFieldValue('image' as never, documentId);
    }
  }, [isOpenModal, documentId, setFieldValue]);

  useEffect(() => {
    if (instructionId) {
      (async () => {
        const scriptData = parseJsonToEditorStateWithInstruction(getValues()[scriptFieldName]);
        const instruction = scriptData.instructions.find(
          (item) => item.id === instructionId && item.type === InstructionTypes.imageInstruction,
        );

        if (instruction) {
          const { data } = instruction as ImageInstruction;
          setComment(instruction.comment);
          setScreenLocation(data.screenLocation);
          setImageEffect(data.imageEffect);
          setDocumentId(data.documentId);
          setInitialState({
            comment: instruction.comment,
            screenLocation: data.screenLocation,
            imageEffect: data.imageEffect,
            documentId: data.documentId,
          });
        } else {
          setComment(defaultState.comment);
          setScreenLocation(defaultState.screenLocation);
          setImageEffect(defaultState.imageEffect);
          setDocumentId(defaultState.documentId);
          setInitialState(defaultState);
        }
      })();
    } else {
      setComment(defaultState.comment);
      setScreenLocation(defaultState.screenLocation);
      setImageEffect(defaultState.imageEffect);
      setDocumentId(defaultState.documentId);
      setInitialState(defaultState);
    }
  }, [
    getValues,
    instructionId,
    setComment,
    setScreenLocation,
    setImageEffect,
    setDocumentId,
    setInitialState,
  ]);

  useEffect(() => {
    if (!images.loading) {
      (async () => {
        const { data } = currentProject;
        if (data) {
          const params: ListParams = {
            sort_dir: 'DESC',
            page_size: 50,
            filter: {
              project_id: data.id,
              is_media: 1,
              media_type_id: 1,
            },
          };
          if (images.data === null) {
            dispatch(getImageListRequest(params));
          } else {
            const projectId = getQueryFilter('project_id', images.data.filters.active);

            if (projectId !== undefined && String(projectId.value) !== String(data.id)) {
              dispatch(getImageListRequest(params));
            }
          }
        }
      })();
    }
  }, [dispatch, images, currentProject]);

  return (
    <ModalComponent
      open={isOpenModal}
      title={getSummary()}
      saveHandler={saveImageInstruction}
      cancelHandler={cancelImageInstruction}
      deleteHandler={removeHandler}
      duplicate={duplicate}
      showKebabMenu={showKebabMenu}
    >
      <Grid container spacing={3} justify="space-around">
        <Grid item xs={12}>
          <InputComponent
            type="textarea"
            label="Comment"
            value={comment}
            onChange={setComment}
            autoFocus
          />
        </Grid>
        <Grid item container spacing={3} xs={6} alignContent="flex-start">
          <Grid item xs={12} className={classes.selectImageWrapper}>
            <FormSimpleAutoComplete
              name="image"
              label="Image"
              options={imageList}
              callback={onDocumentChangeHandler}
              isClearValueIfOptionNull
            />
          </Grid>
          <Grid item xs={12}>
            <SelectComponent
              label="Screen Location"
              value={screenLocation}
              items={ScreenLocations}
              changeHandler={setScreenLocation}
              isNone={false}
            />
          </Grid>
          <Grid item xs={12}>
            <SelectComponent
              label="Effects"
              value={imageEffect}
              items={ImageEffects}
              changeHandler={setImageEffect}
              isNone={false}
            />
          </Grid>
        </Grid>
        <Grid item container spacing={3} xs={6} alignItems="flex-start" alignContent="flex-start">
          <p className={classes.imageTitle}>Preview</p>
          <Grid item container xs={12} alignItems="center" justify="center" className={classes.previewContainer}>
            {
              documentId < 0
                ? <MediaFileMissingMessage />
                : (
                  <>
                    <img onClick={openPreview} src={imageUrl} alt="" className={classes.image} />
                    {(!documentId && documentId !== -1) && <ProcessingMedia />}
                  </>
                )
            }
          </Grid>
        </Grid>
      </Grid>
    </ModalComponent>
  );
};

export default ImageInstructionModal;
